import { MDBTable, MDBTableBody, MDBTableHead } from "mdb-react-ui-kit";
import React, { useEffect, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useDispatch, useSelector } from "react-redux";
import useHasPermission from "../hooks/useHasPermission";
import { PERMISSIONS } from "../constants/permissions";
import {
  fetchInvestmentRates,
  fetchInvestmentCurrencies,
} from "../actions/investmentActions";
import Header from "../components/Header";
import Loader from "../components/Loader";
import SideNav from "../components/SideNav";
import UpdateRateModal from "../components/UpdateRateModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { Link } from "react-router-dom";

const InvestmentRatesScreen = () => {
  const dispatch = useDispatch();
  const ratesRef = useRef(true);
  const [isOpen, setIsOpen] = useState(false);
  const [id, setId] = useState("");
  const [filters, setFilters] = useState({
    currencyId: "",
    isUpfront: "",
  });

  useEffect(() => {
    if (ratesRef.current) {
      dispatch(fetchInvestmentRates());
      dispatch(fetchInvestmentCurrencies());
      ratesRef.current = false;
    }
    setId(id);
  }, [dispatch, id]);

  const investmentRates = useSelector((state) => state.investmentRates);
  const { loading, success, error, rates } = investmentRates;

  const investmentCurrencies = useSelector(
    (state) => state.getInvestmentCurrencies,
  );
  const { data: currenciesData } = investmentCurrencies || {};
  const currencies = currenciesData?.data || [];

  const admin2faLogin = useSelector((state) => state.admin2faLogin);
  const { userInfo } = admin2faLogin;
  const canEditRates = useHasPermission(PERMISSIONS.EDIT_RATES);

  const revealModal = (id) => {
    setIsOpen(true);
    setId(id);
  };

  const handleFilterChange = (field, value) => {
    setFilters((prev) => ({ ...prev, [field]: value }));
  };

  const handleApplyFilters = () => {
    dispatch(fetchInvestmentRates(filters));
  };

  const handleClearFilters = () => {
    setFilters({ currencyId: "", isUpfront: "" });
    dispatch(fetchInvestmentRates());
  };

  return (
    <>
      <SideNav />
      <Helmet>
        <title>PennyTree - Investment Rates</title>
      </Helmet>
      <section className="page-container">
        <div className="page-content">
          <Header />
          <div className="page-body">
            <h1>Investment Rates</h1>

            {canEditRates && (
              <div
                style={{
                  display: "flex",
                  justifyContent: "flex-end",
                  alignItems: "center",
                  marginBottom: "10px",
                }}
              >
                <div className="button">
                  <FontAwesomeIcon />
                  <Link to="/rates/all">Product Rates</Link>
                </div>
              </div>
            )}

            {/* Filters */}
            <div
              style={{
                background: "#1a1a1a",
                borderRadius: "8px",
                padding: "16px 20px",
                marginBottom: "20px",
                display: "flex",
                gap: "16px",
                alignItems: "flex-end",
                flexWrap: "wrap",
              }}
            >
              {/* Currency Filter */}
              <div className="form-group" style={{ margin: 0 }}>
                <label
                  style={{
                    fontSize: "12px",
                    marginBottom: "4px",
                    display: "block",
                    letterSpacing: "1px",
                    textTransform: "uppercase",
                  }}
                >
                  Currency
                </label>
                <select
                  className="invest-input"
                  value={filters.currencyId}
                  onChange={(e) =>
                    handleFilterChange("currencyId", e.target.value)
                  }
                  style={{ width: "180px", padding: "8px 12px", color: "#000" }}
                >
                  <option value="">All Currencies</option>
                  {currencies?.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.short_name} — {c.name}
                    </option>
                  ))}
                </select>
              </div>

              {/* Interest Upfront Filter */}
              <div className="form-group" style={{ margin: 0 }}>
                <label
                  style={{
                    fontSize: "12px",
                    marginBottom: "4px",
                    display: "block",
                    letterSpacing: "1px",
                    textTransform: "uppercase",
                  }}
                >
                  Interest Upfront
                </label>
                <select
                  className="invest-input"
                  value={filters.isUpfront}
                  onChange={(e) =>
                    handleFilterChange("isUpfront", e.target.value)
                  }
                  style={{ width: "180px", padding: "8px 12px", color: "#000" }}
                >
                  <option value="">Interest Upfront Rates</option>
                  <option value="true">Yes</option>
                  <option value="false">No</option>
                </select>
              </div>

              {/* Buttons */}
              <button
                className="login-btn"
                onClick={handleApplyFilters}
                style={{
                  padding: "8px 20px",
                  width: "100px",
                  marginBottom: "0",
                }}
              >
                Apply
              </button>
              {(filters.currencyId || filters.isUpfront !== "") && (
                <button
                  onClick={handleClearFilters}
                  style={{
                    padding: "8px 16px",
                    background: "transparent",
                    border: "1px solid #ccc",
                    borderRadius: "4px",
                    cursor: "pointer",
                    color: "#fff",
                  }}
                >
                  Clear
                </button>
              )}
            </div>

            {error && <p className="text-danger">{error}</p>}

            <>
              {isOpen && <UpdateRateModal setIsOpen={setIsOpen} id={id} />}
              {loading ? (
                <Loader />
              ) : (
                <MDBTable
                  responsive
                  style={{ width: "100%", fontSize: "13px" }}
                >
                  <MDBTableHead>
                    <tr className="font-reduce">
                      <th>S/N</th>
                      <th>Name</th>
                      <th>Description</th>
                      <th>Currency</th>
                      <th>Duration</th>
                      <th>Rate</th>
                      <th>Daily Interest Rate</th>
                      <th>Liquidation Fee</th>
                      <th>Liquidation Fee Cap</th>
                      <th>Interest Upfront</th>
                      <th>Active</th>
                      <th>Date Created</th>
                      <th>Action</th>
                    </tr>
                  </MDBTableHead>
                  <MDBTableBody>
                    {success &&
                      rates &&
                      rates?.items?.map((r, key) => (
                        <tr key={r?.id}>
                          <td>{key + 1}</td>
                          <td>{r?.name}</td>
                          <td>{r?.description}</td>
                          <td>
                            <span style={{ fontWeight: "600" }}>
                              {r?.currency_name}
                            </span>
                            <br />
                            <span style={{ fontSize: "11px", color: "#aaa" }}>
                              {r?.currency_full_name}
                            </span>
                          </td>
                          <td>{r?.duration} days</td>
                          <td>{r?.rate}%</td>
                          <td>{r?.daily_interest_rate?.toFixed(5)}</td>
                          <td>{r?.liquidation_fee?.toFixed(2)}</td>
                          <td>{r?.liquidation_fee_cap?.toFixed(2)}</td>
                          <td>
                            <span
                              style={{
                                padding: "2px 10px",
                                borderRadius: "12px",
                                fontSize: "11px",
                                fontWeight: "600",
                                background: r?.interest_upfront
                                  ? "rgba(76,200,0,0.15)"
                                  : "rgba(108,117,125,0.15)",
                                color: r?.interest_upfront
                                  ? "#4cc800"
                                  : "#6c757d",
                              }}
                            >
                              {r?.interest_upfront ? "Yes" : "No"}
                            </span>
                          </td>
                          <td>
                            <span
                              style={{
                                padding: "2px 10px",
                                borderRadius: "12px",
                                fontSize: "11px",
                                fontWeight: "600",
                                background: r?.is_active
                                  ? "rgba(76,200,0,0.15)"
                                  : "rgba(220,53,69,0.15)",
                                color: r?.is_active ? "#4cc800" : "#dc3545",
                              }}
                            >
                              {r?.is_active ? "Active" : "Inactive"}
                            </span>
                          </td>
                          <td style={{ fontSize: "11px", color: "#aaa" }}>
                            {r?.date_created
                              ? new Date(r.date_created).toLocaleDateString(
                                  "en-NG",
                                  {
                                    year: "numeric",
                                    month: "short",
                                    day: "numeric",
                                  },
                                )
                              : "—"}
                          </td>
                          <td>
                            <button
                              className="withdrawal-btn"
                              onClick={() => revealModal(r?.id)}
                            >
                              Update
                            </button>
                          </td>
                        </tr>
                      ))}
                  </MDBTableBody>
                </MDBTable>
              )}
            </>
          </div>
        </div>
      </section>
    </>
  );
};

export default InvestmentRatesScreen;
